import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Customer, User } from '../../hooks/useApi/types'
import { useAnalyticsFiltersStore } from '../../features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import useAnalyticsStore from '../../features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { useBillingFiltersStore } from '../../features/BillingPayments/stores/useBillingFiltersStore/useBillingFiltersStore'
import { useSustainabilityFiltersStore } from '@/features/Sustainability/hooks/useSustainabilityFiltersStore'
import useSustainabilityStore from '@/features/Sustainability/stores/useSustainabilityStore/useSustainabilityStore'
import { useDashboardUsageAndCostFiltersStore } from '@/stores/useDashboardUsageAndCostFiltersStore'

export type Session = {
    accessToken: string
    refreshToken: string
    sessionId: string
}

export type AuthStoreState = Session & {
    setSession: (session: Session) => void
    user: User | null
    setUser: (user: User | null) => void
    loadSession: () => void
    clearSession: () => void
    customer: Customer | null
    setCustomer: (customer: Customer | null) => void
}

export const SESSION_STORAGE_KEY = 'authSession'

const initialSessionState: Session = {
    accessToken: '',
    refreshToken: '',
    sessionId: '',
}

export const useAuthStore = create<AuthStoreState>()(
    persist(
        (set) => {
            const { resetActiveDataSets } =
                useAnalyticsStore.getState().usageAndCost
            const { resetReportSettings } =
                useAnalyticsStore.getState().benchmarking
            const { resetActiveFilters: resetAnalyticsFilters } =
                useAnalyticsFiltersStore.getState()
            const { resetActiveFilters: resetBillingFilters } =
                useBillingFiltersStore.getState()
            const { resetActiveFilters: resetSustainabilityFilters } =
                useSustainabilityFiltersStore.getState()
            const { resetActiveFilters: resetDashboardFilters } =
                useDashboardUsageAndCostFiltersStore.getState()
            const { resetReportSettings: resetTotalEmissionsReportSettings } =
                useSustainabilityStore.getState().totalEmissions
            const { resetReportSettings: resetGHGEmissionsReportSettings } =
                useSustainabilityStore.getState().ghgEmissions
            const { resetReportSettings: resetElectricityMixReportSettings } =
                useSustainabilityStore.getState().electricityMix

            const loadSession = (): Session | null => {
                const savedSession = localStorage.getItem(SESSION_STORAGE_KEY)
                return savedSession
                    ? (JSON.parse(savedSession) as Session)
                    : null
            }

            const clearSession = () => {
                set(() => ({
                    ...initialSessionState,
                    user: null,
                    customer: null,
                }))
                localStorage.removeItem(SESSION_STORAGE_KEY)
                resetAnalyticsFilters()
                resetBillingFilters()
                resetSustainabilityFilters()
                resetDashboardFilters()
                resetActiveDataSets()
                resetReportSettings()
                resetTotalEmissionsReportSettings()
                resetGHGEmissionsReportSettings()
                resetElectricityMixReportSettings()
            }

            const loadedSession = loadSession() || initialSessionState

            return {
                accessToken: loadedSession.accessToken,
                refreshToken: loadedSession.refreshToken,
                sessionId: loadedSession.sessionId,
                user: null,
                customer: null,

                setSession: ({ accessToken, refreshToken, sessionId }) => {
                    const session = { accessToken, refreshToken, sessionId }
                    set(() => session)
                    localStorage.setItem(
                        SESSION_STORAGE_KEY,
                        JSON.stringify(session)
                    )
                },

                setUser: (user) => {
                    set(() => ({ user }))
                },

                setCustomer: (customer) => {
                    const prevCustomer = useAuthStore.getState().customer
                    set(() => ({ customer }))
                    const hasCustomerChanged =
                        prevCustomer?.customerId !== customer?.customerId

                    if (hasCustomerChanged) {
                        resetAnalyticsFilters()
                        resetBillingFilters()
                        resetSustainabilityFilters()
                        resetDashboardFilters()
                        resetActiveDataSets()
                        resetReportSettings()
                        resetTotalEmissionsReportSettings()
                        resetGHGEmissionsReportSettings()
                        resetElectricityMixReportSettings()
                    }
                },

                loadSession: () => {
                    loadSession()
                },

                clearSession: () => {
                    clearSession()
                },
            }
        },
        {
            name: 'auth-zustand',
            partialize: (state) => ({
                customer: state.customer,
            }),
        }
    )
)

export default useAuthStore
